// Shared dictionary-style brand intro. Used on both the Splash component
// (briefly, on first-session app load) and the /welcome page (permanent
// destination linked from auth pages).
//
// Typography: serif for the body so it reads as a real dictionary entry,
// not a marketing tagline. The headword + IPA pronunciation get the
// proper grammatical typography.

import { Logo } from './ui/Logo';

const SERIF_STACK = `'Iowan Old Style', 'Charter', 'Cambria', 'Georgia', serif`;

export function DictionaryEntry({
  size = 'lg',
  animatedLogo = true,
}: {
  size?: 'md' | 'lg';
  animatedLogo?: boolean;
}) {
  const logoSize = size === 'lg' ? 84 : 64;
  const headingSize = size === 'lg' ? '40px' : '32px';
  return (
    <div style={{ textAlign: 'center', maxWidth: 560 }}>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-6)' }}>
        <Logo size={logoSize} animated={animatedLogo} />
      </div>

      <h1
        style={{
          fontFamily: SERIF_STACK,
          fontSize: headingSize,
          fontWeight: 600,
          letterSpacing: '-0.02em',
          marginBottom: 'var(--space-2)',
        }}
      >
        Rouser
      </h1>

      <div
        style={{
          color: 'var(--color-text-secondary)',
          fontSize: 'var(--fs-md)',
          marginBottom: 'var(--space-8)',
        }}
      >
        <span className="ds-mono">/ˈraʊzə/</span>
        <span style={{ margin: '0 var(--space-2)' }}>·</span>
        <span style={{ fontStyle: 'italic', fontFamily: SERIF_STACK }}>noun</span>
      </div>

      <ol
        style={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          textAlign: 'left',
          fontFamily: SERIF_STACK,
          fontSize: 'var(--fs-lg)',
          lineHeight: 1.6,
          color: 'var(--color-text)',
          counterReset: 'def',
        }}
      >
        <Definition n={1}>
          A person or thing that rouses, excites, or stirs others into action — especially out of
          sleep, distraction, or complacency.
        </Definition>
        <Definition n={2}>
          An incident-response platform that ensures the right people are awake when it matters.
        </Definition>
      </ol>
    </div>
  );
}

function Definition({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li
      style={{
        display: 'grid',
        gridTemplateColumns: '32px 1fr',
        columnGap: 'var(--space-4)',
        marginBottom: 'var(--space-4)',
      }}
    >
      <span
        aria-hidden
        style={{
          fontFamily: 'var(--font-sans)',
          fontVariantNumeric: 'tabular-nums',
          color: 'var(--color-accent-soft-text)',
          fontSize: 'var(--fs-sm)',
          fontWeight: 600,
          paddingTop: '6px',
          textAlign: 'right',
        }}
      >
        {n}.
      </span>
      <span>{children}</span>
    </li>
  );
}
